import React, { useState } from 'react'
import useMedia from 'use-media'
import { FlyToInterpolator } from 'react-map-gl'
import template from 'lodash.template'
import { faMapMarkerAlt, faTimes } from '@fortawesome/free-solid-svg-icons'

import Theme from '../../atoms/Theme'
import Heading from '../../atoms/Typography/Heading'
import Container from '../../atoms/Container'
import Row from '../../atoms/Row'
import Col from '../../atoms/Col'
import Placeholder from '../../atoms/Placeholder'
import Button from '../../atoms/Button/Button'
import Description from '../../atoms/LandingPage/Description'
import Markdown from '../../atoms/Markdown'
import MobileFixed from '../../atoms/LandingPage/MobileFixed'
import ButtonGroup from '../../atoms/LandingPage/ButtonGroup'
import Link from '../../atoms/Link'
import Icon from '../../atoms/Icon'

import Breadcrumb from '../../molecules/LandingPage/Breadcrumb'
import RelatedLinks from '../../molecules/RelatedLinks'

import Navbar from '../../organisms/Navbar'
import Footer from '../../organisms/Footer'
import Card from '../../organisms/ApartmentCardVersion2'
import Map from '../../organisms/Map'
import CopyTop from '../../organisms/CopyTop'

import Page from '../Blank/Page'

import Main from './Main'
import Aside from './Aside'
import CardWrapper from './CardWrapper'

import { getSearchUrl } from '../../helpers'

export default props => {
  const {
    trackingData,
    menu,
    logo,
    footer,
    apartments: { count },
    h1,
    breadcrumb,
    copy,
    pricingDisclaimer,
    hideSubmarkets,
    noindex,
    copyTop,
    heroImage,
    version,
  } = props
  const { market, submarket = {}, submarkets } = breadcrumb

  // TODO: card phone number and website url should be based on source/medium from user's first interaction
  const apartments = props.apartments.items.map(apartment => ({
    ...apartment,
    prospectPhoneNumber:
      apartment[noindex ? 'ppcProspectPhoneNumber' : 'prospectPhoneNumber'] ||
      apartment.prospectPhoneNumber,
    marketingWebsiteUrl:
      apartment[noindex ? 'ppcMarketingWebsiteUrl' : 'marketingWebsiteUrl'] ||
      apartment.marketingWebsiteUrl,
  }))

  const [show, setShow] = useState()
  const [loaded, setLoaded] = useState()
  const [viewport, setViewport] = useState({})
  const [popup, setPopup] = useState()
  const [highlight, setHighlight] = useState()

  const isMobile = useMedia({ maxWidth: '767.9px' })
  const isLarge = useMedia({ minWidth: '992px' })

  const toggleMap = () => {
    setShow(show === 'map' ? '' : 'map')
    setLoaded(true)
  }

  const closePopup = () => setPopup()
  const showPopup = apartment => {
    setPopup(apartment)
    const offset = 105 / 2 ** viewport.zoom
    setViewport({
      ...viewport,
      transitionDuration: 1000,
      transitionInterpolator: new FlyToInterpolator(),
      latitude: apartment.coordinates.lat + offset,
      longitude: apartment.coordinates.lng,
    })
  }

  return (
    <Theme>
      <Page trackingData={trackingData}>
        <Navbar {...{ menu, logo }} />
        <Main>
          <Container>
            <Row as='section'>
              <Col>
                <ButtonGroup>
                  <Breadcrumb {...breadcrumb} />
                  <MobileFixed>
                    {noindex ? null : <Button as={Link} href={getSearchUrl(breadcrumb)} type='secondary'>
                      Filter
                    </Button>}
                    {isMobile ? (
                      <Button type='secondary' onClick={toggleMap}>
                        <Icon fixedWidth icon={show === 'map' ? faTimes : faMapMarkerAlt} />
                      </Button>
                    ) : null}
                  </MobileFixed>
                </ButtonGroup>
                <Heading>{template(h1)(props)}</Heading>
                <p>
                  Viewing {count} apartments in {(submarket || market).title}
                </p>
                <CopyTop {...{ copyTop, heroImage }} />
                <small>{pricingDisclaimer}</small>
                <Row>
                  {apartments.map((apartment, i) => (
                    <CardWrapper
                      key={i}
                      fullWidth={isLarge && count <= 3 && version !== 1}
                      onMouseEnter={() => setHighlight(apartment)}
                      onMouseLeave={() => setHighlight()}
                      version={version}
                    >
                      <Card
                        position={i}
                        {...apartment}
                        showPopup={isMobile ? null : () => showPopup(apartment)}
                        isGrid={!isLarge || count > 3 || version === 1}
                        version={version}
                      />
                    </CardWrapper>
                  ))}
                </Row>
                {copy ? (
                  <Description>
                    <Markdown children={copy} />
                  </Description>
                ) : null}
                <RelatedLinks {...market} />
                <RelatedLinks {...submarket} />
                <RelatedLinks
                  title={`Neighborhoods in ${market.title}`}
                  nonMarketPages={submarkets
                    .filter(sm => sm.marketPage && sm.marketPage.apartments.count > 0)
                    .map(sm => ({ ...sm.marketPage, shortTitle: sm.marketPage.shorTitle || sm.title }))
                  }
                />
              </Col>
            </Row>
          </Container>
        </Main>
        <Aside show={show === 'map'}>
          {!isMobile || show === 'map' || loaded ? (
            <Map
              onLoad={() => setLoaded(true)}
              {...{
                viewport,
                setViewport,
                hideSubmarkets: !submarket || hideSubmarkets,
                highlight,
                submarkets: submarkets.filter(
                  sm =>
                    !(
                      submarket &&
                      submarket.marketPage &&
                      sm.marketPage &&
                      submarket.marketPage.slug === sm.marketPage.slug
                    ),
                ),
                apartments,
                popup,
                showPopup,
                closePopup,
              }}
            />
          ) : (
            <Placeholder />
          )}
        </Aside>
        <Footer width={0.6667} {...footer} />
      </Page>
    </Theme>
  )
}
